import { useTranslation } from 'react-i18next';
import {
  Card,
  Loader,
  ModalComponentWrapper,
  UserProfileAction,
  Switch,
} from '@/shared/components';
import { tv } from 'tailwind-variants';
import AuthorizationIcon from '@/shared/assets/authorization-icon.svg?react';
import EditIcon from '@/shared/assets/edit-icon.svg?react';
import SmsIcon from '@/shared/assets/sms-icon.svg?react';
import EmailIcon from '@/shared/assets/email-icon.svg?react';
import {
  useTwoFactorAuthIdentities,
  MFA_IDENTITY_TYPES,
  type TwoFactorIdentity,
  type MFAIdentityType,
} from '@/entities/mfa-identity';
import { useMemo } from 'react';
import { useConfirmationModal } from '@/widgets';
import {
  useTwoFactorSettingsModals,
  type TwoFactorSettingsModalArgs,
} from '../model';
import { disableTwoFactorIdentity } from '../api';
import type { DisableTwoFactorIdentityInitialResponseDto } from '../api/types';
import { useUserProfileInfo } from '@/entities/user';
import { useContactUs } from '@/shared/lib';

const twoFactorAuthSettingsClasses = tv({
  slots: {
    card: 'py-2 px-0 rounded-[1.6rem] w-full shadow-none flex flex-col gap-2',
    icon: 'size-10 text-primary',
  },
});

const TwoFactorSettingsModal = ({
  onBack,
  goToCurrentScreen,
}: TwoFactorSettingsModalArgs) => {
  const { t } = useTranslation();
  const localePath =
    'personalAccount.userProfile.securityAndPrivacy.twoFactorAuthorization';
  const { card, icon } = twoFactorAuthSettingsClasses();
  const { twoFactorAuthIdentities, isTwoFactorAuthIdentitiesLoading } =
    useTwoFactorAuthIdentities();
  const {
    openTwoFactorSettingsModal,
    openTwoFactorAppEnableModal,
    openTwoFactorIdentityEnableModal,
    openTwoFactorIdentityDisableModal,
  } = useTwoFactorSettingsModals();
  const { openModal, Modal: ConfirmationModal } = useConfirmationModal();
  const { userProfileInfoMap } = useUserProfileInfo();
  const { links } = useContactUs();
  const userEmail = userProfileInfoMap?.['email'];
  const userPhone = userProfileInfoMap?.['phone'];

  const { googleauthenticator, email, phone } = useMemo(() => {
    const defaultIdentities = {
      [MFA_IDENTITY_TYPES.GOOGLE_AUTHENTICATOR]: undefined,
      [MFA_IDENTITY_TYPES.EMAIL]: undefined,
      [MFA_IDENTITY_TYPES.PHONE]: undefined,
    };
    if (!twoFactorAuthIdentities) return defaultIdentities;

    return twoFactorAuthIdentities.reduce(
      (acc, identity) => {
        acc[identity.identityTypeName] = identity;
        return acc;
      },
      defaultIdentities as Record<
        MFAIdentityType,
        TwoFactorIdentity | undefined
      >
    );
  }, [twoFactorAuthIdentities]);

  const handleTwoFactorIdentityChange = (
    identityType: MFAIdentityType,
    checked: boolean
  ) => {
    if (!checked) {
      openModal({
        title: t(`${localePath}.disableTwoFactorIdentity.title`),
        description: t(`${localePath}.disableTwoFactorIdentity.description`),
        confirmText: t(`${localePath}.disableTwoFactorIdentity.confirmText`),
        cancelText: t(`${localePath}.disableTwoFactorIdentity.cancelText`),
        onConfirm: async () => {
          try {
            const response = await disableTwoFactorIdentity(identityType, {});
            const {
              data: { state },
            } = response as DisableTwoFactorIdentityInitialResponseDto;
            openTwoFactorIdentityDisableModal({
              identityType,
              state,
              onBack: goToCurrentScreen,
              onSuccess: goToCurrentScreen,
            });
          } catch (error) {
            console.error(error);
          }
        },
      });
    } else {
      switch (identityType) {
        case MFA_IDENTITY_TYPES.GOOGLE_AUTHENTICATOR:
          openTwoFactorAppEnableModal({
            onBack: goToCurrentScreen,
            onSuccess: () => {
              openTwoFactorSettingsModal({ onBack });
            },
          });
          break;
        case MFA_IDENTITY_TYPES.EMAIL:
          openTwoFactorIdentityEnableModal({
            identityType,
            identity: userEmail,
            onBack: goToCurrentScreen,
            onSuccess: goToCurrentScreen,
          });
          break;
        case MFA_IDENTITY_TYPES.PHONE:
          openTwoFactorIdentityEnableModal({
            identityType,
            identity: userPhone,
            onBack: goToCurrentScreen,
            onSuccess: goToCurrentScreen,
          });
          break;
        default:
          break;
      }
    }
  };

  return (
    <ModalComponentWrapper
      title={t(`${localePath}.title`)}
      onBack={onBack}
      headerClassName="text-secondary-bg bg-secondary-bg/50">
      {isTwoFactorAuthIdentitiesLoading ? (
        <div className="flex items-center justify-center min-h-[20rem]">
          <Loader />
        </div>
      ) : (
        <>
          <Card className={card()}>
            {googleauthenticator && (
              <UserProfileAction
                icon={<AuthorizationIcon className={icon()} />}
                title={t(`${localePath}.enableViaApp`)}>
                <Switch
                  checked={googleauthenticator.isEnabled}
                  onCheckedChange={checked => {
                    handleTwoFactorIdentityChange(
                      googleauthenticator.identityTypeName,
                      checked
                    );
                  }}
                />
              </UserProfileAction>
            )}
            <UserProfileAction
              icon={<EditIcon className={icon()} />}
              title={t(`${localePath}.lostAccess`)}
              showChevron={false}
              onClick={() => {
                window.open(
                  links?.turnoffTwofaRequest || links?.defaultRequest || '',
                  '_blank'
                );
              }}
            />
          </Card>
          {twoFactorAuthIdentities && twoFactorAuthIdentities.length > 1 && (
            <div>
              <div className="p4 text-secondary mb-[1.2rem]">
                {t(`${localePath}.additionalMethods`).toUpperCase()}
              </div>
              <Card className={card()}>
                {phone && (
                  <UserProfileAction
                    icon={<SmsIcon className={icon()} />}
                    title={t(`${localePath}.smsCode`)}>
                    <Switch
                      checked={phone.isEnabled}
                      onCheckedChange={checked => {
                        handleTwoFactorIdentityChange(
                          phone.identityTypeName,
                          checked
                        );
                      }}
                    />
                  </UserProfileAction>
                )}
                {email && (
                  <UserProfileAction
                    icon={<EmailIcon className={icon()} />}
                    title={t(`${localePath}.emailCode`)}>
                    <Switch
                      checked={email.isEnabled}
                      onCheckedChange={checked => {
                        handleTwoFactorIdentityChange(
                          email.identityTypeName,
                          checked
                        );
                      }}
                    />
                  </UserProfileAction>
                )}
              </Card>
            </div>
          )}
        </>
      )}
      {ConfirmationModal}
    </ModalComponentWrapper>
  );
};

export { TwoFactorSettingsModal };
